import { useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
  Button,
  Drawer,
  ScrollShadow,
  Separator,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@xstore/shared/ui';
import {
  X,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

import { NAV_GROUPS, NAV_MODULES } from '../../config/navigation';
import { useBots } from '../../providers/bot-provider';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SidebarContentProps {
  collapsed: boolean;
  onNavigate?: () => void;
}

function StatusDot({ mode }: { mode: string }) {
  const color =
    mode === 'ONLINE'
      ? 'bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]'
      : mode === 'OFFLINE'
      ? 'bg-red-500'
      : mode === 'MAINTENANCE'
      ? 'bg-amber-500'
      : 'bg-slate-500';
  return <span className={`h-2 w-2 rounded-full ${color}`} />;
}

function WorkspaceHeader({ collapsed }: { collapsed: boolean }) {
  const { selectedBot } = useBots();
  return (
    <div className="px-3 py-4">
      <div
        className="flex items-center gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.03] p-3 transition-all duration-300"
        style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}
      >
        <div className="relative shrink-0">
          <img
            src="/x-store.png"
            alt="X Store"
            className="h-9 w-9 rounded-xl object-contain"
          />
          <div className="absolute -bottom-0.5 -right-0.5 rounded-full border border-background bg-background p-0.5">
            <StatusDot mode={selectedBot?.mode ?? 'OFFLINE'} />
          </div>
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">X Store</p>
            <p className="truncate text-xs text-muted">
              {selectedBot?.name ?? 'No bot connected'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function SidebarContent({ collapsed, onNavigate }: SidebarContentProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const grouped = NAV_GROUPS.map((g) => ({
    ...g,
    items: NAV_MODULES.filter((m) => m.group === g.id),
  }));

  const handleClick = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  return (
    <div className="flex h-full flex-col">
      <WorkspaceHeader collapsed={collapsed} />
      <Separator className="mx-3 w-[calc(100%-24px)]" />

      <ScrollShadow className="flex-1 px-3 py-3">
        {grouped.map((group) => (
          <div key={group.id} className="mb-4">
            {!collapsed && (
              <p className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted/80">
                {group.label}
              </p>
            )}
            <div className="space-y-1">
              {group.items.map((mod) => {
                const Icon = mod.icon;
                const isActive =
                  location.pathname === mod.path ||
                  (mod.path !== '/' && location.pathname.startsWith(mod.path));

                const button = (
                  <Button
                    key={mod.id}
                    variant="ghost"
                    onPress={() => handleClick(mod.path)}
                    className={[
                      'group relative flex h-auto w-full items-center justify-start gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground/80 hover:bg-white/[0.05] hover:text-foreground',
                    ].join(' ')}
                  >
                    {isActive && (
                      <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary" />
                    )}
                    <Icon size={18} />
                    {!collapsed && <span className="truncate">{mod.label}</span>}
                    {!collapsed && isActive && (
                      <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary/80" />
                    )}
                  </Button>
                );

                if (collapsed) {
                  return (
                    <Tooltip key={mod.id}>
                      <TooltipTrigger>{button}</TooltipTrigger>
                      <TooltipContent className="z-50">
                        {mod.label}
                      </TooltipContent>
                    </Tooltip>
                  );
                }
                return button;
              })}
            </div>
          </div>
        ))}
      </ScrollShadow>
    </div>
  );
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className="sticky top-0 hidden h-screen flex-shrink-0 border-r border-white/[0.06] bg-background/95 backdrop-blur-xl transition-all duration-300 lg:flex"
        style={{ width: collapsed ? '80px' : '260px' }}
      >
        <div className="flex w-full flex-col">
          <SidebarContent collapsed={collapsed} />
          <div className="mt-auto px-3 pb-4">
            <Button
              isIconOnly={!collapsed}
              variant="ghost"
              className="w-full justify-center gap-2"
              onPress={() => setCollapsed((prev) => !prev)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
              {!collapsed && <span className="text-sm">Collapse</span>}
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile drawer */}
      <Drawer isOpen={isOpen} onOpenChange={onClose}>
        <Drawer.Backdrop />
        <Drawer.Content className="w-80">
          <Drawer.Dialog>
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2">
                  <img src="/x-store.png" alt="X Store" className="h-8 w-8 rounded-lg" />
                  <span className="text-lg font-bold">X Store</span>
                </div>
                <Button isIconOnly variant="ghost" onPress={onClose}>
                  <X size={18} />
                </Button>
              </div>
              <Separator />
              <SidebarContent collapsed={false} onNavigate={onClose} />
            </div>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer>
    </>
  );
}
